import * as datadogRum from 'lib/telemetry/datadogRum';

/**
 * Per-view main-thread blocking rollup.
 *
 * Answers "did this page freeze, and what froze it" as a handful of view attributes
 * that can be grouped by the page-shape fingerprint (`market_metadata.*`) — e.g.
 * `p75(@context.main_thread.total_blocking_ms)` by `chart_type`.
 *
 * Deliberately an aggregate, not the SDK's `trackLongTasks`. That option emits one RUM
 * event per long task; on a WebSocket-driven trading page at 100% session sampling that
 * is a large, unbounded volume — and it still could not be grouped by chart type, which
 * is the entire question. A bounded per-view summary costs four numbers per view.
 *
 * Split into observation and publication on purpose. `initDatadog()` runs from a passive
 * effect (`useRouteLogic`), i.e. after first paint, so wiring the observer there would
 * miss exactly the cold-start work behind the LCP tail. `startMainThreadObservation()`
 * therefore installs the observer at the earliest client seam
 * (`src/instrumentation-client.ts`) and accumulates into a plain object; publication is
 * attached later, once RUM exists, and picks up everything already accumulated.
 */

type ObserverType = 'loaf' | 'longtask' | 'none';

/** Below this, the frame is not considered blocking (the long-task threshold). */
const LONG_TASK_THRESHOLD_MS = 50;

/** Minimum gap between steady-state publishes of a still-open view. */
const FLUSH_THROTTLE_MS = 2_000;

const MAX_SCRIPT_LABEL_LENGTH = 120;

interface LoafScript {
    sourceURL?: string;
    invoker?: string;
    duration?: number;
}

interface LoafEntry extends PerformanceEntry {
    blockingDuration?: number;
    scripts?: LoafScript[];
}

interface Accumulator {
    totalBlockingMs: number;
    longTaskCount: number;
    longestMs: number;
    worstScript?: string;
    worstScriptMs: number;
    /** `performance.now()` at which this view's accumulation began. */
    viewStartPerfNow: number;
    /**
     * Whether this view's closing vital has already been emitted. Lives on the
     * accumulator rather than in a set keyed by view token, because at
     * `onBeforeViewChange` time the token has not moved yet — keying by it required
     * predicting the next value, and any drift in that guess silently produced either
     * duplicate or missing vitals.
     */
    vitalEmitted: boolean;
}

export interface MainThreadRollup {
    total_blocking_ms: number;
    long_task_count: number;
    longest_ms: number;
    observer_type: ObserverType;
    supported: boolean;
    worst_script?: string;
    worst_script_ms?: number;
}

let observerType: ObserverType = 'none';
let observer: PerformanceObserver | null = null;
/**
 * Whether an observer has already consumed the buffered backlog in this document.
 * Deliberately NOT reset by `stopMainThreadBlocking()` — it tracks a property of the
 * document, not of the current observation session.
 */
let hasObservedInThisDocument = false;
let observing = false;
let publishing = false;

let accumulator: Accumulator = newAccumulator(0);
let dirty = false;
let lastFlushPerfNow = -Infinity;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * The outgoing view's accumulator, set aside at transition start. See
 * `handleTransitionStart` — the split is what keeps a route's chunk-evaluation cost on
 * the view that caused it.
 */
let stashedOutgoing: Accumulator | null = null;

/**
 * The accumulator `closeCurrentView()` last published, kept only so the close can be
 * undone if the history write it preceded throws. See `revertViewChange`.
 */
let lastClosedView: Accumulator | null = null;
/** Whether that close collapsed a transition's split, which the revert has to put back. */
let lastClosedViewWasStashed = false;

let unsubscribeBeforeViewChange: (() => void) | null = null;
let unsubscribeViewChangeReverted: (() => void) | null = null;
let unsubscribeTransitionStart: (() => void) | null = null;
let unsubscribeTransitionAborted: (() => void) | null = null;
let vitalContextProvider: (() => Record<string, unknown>) | null = null;

function newAccumulator(startPerfNow: number): Accumulator {
    return {
        totalBlockingMs: 0,
        longTaskCount: 0,
        longestMs: 0,
        worstScript: undefined,
        worstScriptMs: 0,
        viewStartPerfNow: startPerfNow,
        vitalEmitted: false,
    };
}

/**
 * Shorten a script URL to something safe to store as a plain attribute: no query or
 * hash (they can carry identifiers), last two path segments only, hard length cap.
 * Never faceted — chunk URLs are high-cardinality and change every deploy.
 */
export function sanitizeScriptLabel(rawUrl: string | undefined, invoker: string | undefined): string | undefined {
    const source = rawUrl?.trim();
    if (!source) return invoker ? invoker.slice(0, MAX_SCRIPT_LABEL_LENGTH) : undefined;

    let path = source;
    try {
        // Relative/opaque values (e.g. "inline") won't parse; fall back to the raw string.
        path = new URL(source, window.location.origin).pathname;
    } catch {
        path = source.split(/[?#]/)[0];
    }

    const segments = path.split('/').filter(Boolean);
    const tail = segments.slice(-2).join('/');
    const label = invoker ? `${tail} (${invoker})` : tail;
    return label.slice(0, MAX_SCRIPT_LABEL_LENGTH) || undefined;
}

/**
 * Blocking time for one entry. The two entry types report different quantities and
 * must not share a formula:
 *  - `long-animation-frame` already exposes `blockingDuration` (the part of the frame
 *    beyond the threshold, across all tasks in the frame).
 *  - `longtask` exposes only total `duration`, whose blocking portion is the excess
 *    over the 50ms threshold.
 */
function blockingMsFor(entry: PerformanceEntry, type: ObserverType): number {
    if (type === 'loaf') {
        const blocking = (entry as LoafEntry).blockingDuration;
        return typeof blocking === 'number' ? Math.max(0, blocking) : 0;
    }
    return Math.max(0, entry.duration - LONG_TASK_THRESHOLD_MS);
}

function recordEntry(entry: PerformanceEntry): void {
    const blockingMs = blockingMsFor(entry, observerType);
    if (blockingMs <= 0) return;

    accumulator.totalBlockingMs += blockingMs;
    accumulator.longTaskCount += 1;
    if (entry.duration > accumulator.longestMs) accumulator.longestMs = entry.duration;

    const scripts = (entry as LoafEntry).scripts;
    if (scripts?.length) {
        for (const script of scripts) {
            const scriptMs = script.duration ?? 0;
            if (scriptMs > accumulator.worstScriptMs) {
                const label = sanitizeScriptLabel(script.sourceURL, script.invoker);
                if (label) {
                    accumulator.worstScriptMs = scriptMs;
                    accumulator.worstScript = label;
                }
            }
        }
    }

    dirty = true;
    scheduleFlush();
}

/**
 * Pull any entries the observer has buffered but not yet delivered, and fold them into
 * the accumulator. Safe to call at any time — `takeRecords()` removes what it returns, so
 * the queued callback cannot double-count them.
 */
function drainObserver(): void {
    const pending = observer?.takeRecords();
    if (!pending?.length) return;
    for (const entry of pending) recordEntry(entry);
}

export function getMainThreadRollup(): MainThreadRollup {
    const rollup: MainThreadRollup = {
        total_blocking_ms: Math.round(accumulator.totalBlockingMs),
        long_task_count: accumulator.longTaskCount,
        longest_ms: Math.round(accumulator.longestMs),
        observer_type: observerType,
        supported: observerType !== 'none',
    };
    if (accumulator.worstScript) {
        rollup.worst_script = accumulator.worstScript;
        rollup.worst_script_ms = Math.round(accumulator.worstScriptMs);
    }
    return rollup;
}

/**
 * Publish the current view's rollup.
 *
 * `emitVital` marks a genuine end of the view (navigation or unload) and is the only
 * thing that emits `view_blocking_time`, so vital volume tracks view count rather than
 * flush count. `force` writes the view context even when nothing new accumulated.
 *
 * Backgrounding is deliberately NOT a view end (`force` without `emitVital`). A user who
 * hides an event-page tab and comes back is still on the same RUM view, and treating the
 * hide as final would burn `vitalEmitted` on a partial measurement and silently drop
 * every long task after the tab was restored — undercounting exactly the long-lived
 * trading sessions most worth measuring. The context write still happens on hide, so if
 * the tab is killed while hidden the numbers survive on the view itself; only the
 * standalone vital is lost, and it is the secondary record of the two.
 */
function publish(emitVital: boolean, force = false): void {
    // PerformanceObserver delivers entries in a queued task, so work done during the
    // navigation click itself — or immediately before `pagehide` — can still be sitting
    // undelivered when a view ends. Left there it would be charged to the NEXT view (the
    // accumulator having been reset in between) or lost entirely on unload, which
    // systematically drops the most expensive frame of the view: the one that ended it.
    drainObserver();

    if (!publishing) return;
    if (!dirty && !force) return;

    const rollup = getMainThreadRollup();
    datadogRum.setViewContextProperty('main_thread', rollup);

    // Quiet views emit too, at duration 0. Skipping them would make the vital a
    // distribution over blocked views only: 75 quiet views plus 25 at 500ms would report a
    // p75 of 500ms instead of 0, and the count would track blocked views rather than
    // views — which is the coverage check this rollout is supposed to pass.
    //
    // `observer_type: 'none'` is the one case that stays silent. On Safari and Firefox a
    // zero is not a measurement, it is the absence of one, and emitting it would seed the
    // distribution with false zeros — the same error as the omission above, inverted.
    if (emitVital && !accumulator.vitalEmitted && observerType !== 'none') {
        accumulator.vitalEmitted = true;
        datadogRum.addDurationVital('view_blocking_time', {
            startTime: datadogRum.epochFromPerfNow(accumulator.viewStartPerfNow),
            duration: Math.round(accumulator.totalBlockingMs),
            context: { ...rollup, ...(vitalContextProvider?.() ?? {}) },
        });
    }

    dirty = false;
    lastFlushPerfNow = performance.now();
}

function scheduleFlush(): void {
    if (!publishing || flushTimer !== null) return;
    const elapsed = performance.now() - lastFlushPerfNow;
    const delay = Math.max(0, FLUSH_THROTTLE_MS - elapsed);
    flushTimer = setTimeout(() => {
        flushTimer = null;
        publish(false);
    }, delay);
}

function clearFlushTimer(): void {
    if (flushTimer !== null) {
        clearTimeout(flushTimer);
        flushTimer = null;
    }
}

/** Fold `source` into `target`. Only used to undo a split, so both describe one view. */
function mergeAccumulators(target: Accumulator, source: Accumulator): void {
    target.totalBlockingMs += source.totalBlockingMs;
    target.longTaskCount += source.longTaskCount;
    target.longestMs = Math.max(target.longestMs, source.longestMs);
    if (source.worstScriptMs > target.worstScriptMs) {
        target.worstScriptMs = source.worstScriptMs;
        target.worstScript = source.worstScript;
    }
}

/**
 * An App Router transition has begun. Everything observed from here belongs to the
 * INCOMING view: evaluating the destination's chunks is the incoming route's cost, and
 * for a heavy chart that is precisely the blocking this instrumentation exists to find.
 * Charging it to the page the user navigated away from would hide it behind whatever
 * they happened to be looking at before.
 *
 * Draining first puts every pre-transition entry on the outgoing view, so the split is
 * exact and no entry has to be divided. It also aligns `view_blocking_time` with
 * `chart_ready`, which already measures from the transition start — two vitals on one
 * view disagreeing about where it began would make them incomparable.
 */
function handleTransitionStart(): void {
    if (!observing) return;
    if (stashedOutgoing) {
        // A second transition before the first settled (rapid clicks). The stash is still
        // the true outgoing view; just re-anchor the incoming one.
        accumulator.viewStartPerfNow = performance.now();
        return;
    }
    drainObserver();
    stashedOutgoing = accumulator;
    accumulator = newAccumulator(performance.now());
}

/**
 * The transition settled without changing the pathname, so no view boundary happened
 * after all. Fold the split back rather than leaving the stash to be published later as
 * if it were a completed view.
 */
function handleTransitionAborted(): void {
    if (!stashedOutgoing) return;
    drainObserver();
    mergeAccumulators(stashedOutgoing, accumulator);
    accumulator = stashedOutgoing;
    stashedOutgoing = null;
}

/**
 * Close out the current view and start a fresh accumulator.
 *
 * Invoked from `onBeforeViewChange`, i.e. while the outgoing view is still the current
 * one — `setViewContextProperty` cannot amend an ended view, so publishing after the
 * navigation would write this view's tail onto the *next* view.
 */
/**
 * Emit the closing rollup for the view that is ending — the STASH when a transition is in
 * flight, since that is the view the user was actually on. Shared by the navigation and
 * unload paths: a `pagehide` during a slow navigation (close or reload mid-transition)
 * would otherwise publish the half-built incoming accumulator and drop the outgoing
 * page's blocking time entirely.
 */
/** Returns the accumulator it published, so `closeCurrentView` can keep it revertible. */
function publishOutgoingView(): Accumulator {
    if (!stashedOutgoing) {
        publish(true, true);
        return accumulator;
    }
    // Drain BEFORE swapping: entries delivered since the transition started belong to the
    // incoming view, and `publish` drains into whatever is current.
    drainObserver();
    const incoming = accumulator;
    accumulator = stashedOutgoing;
    stashedOutgoing = null;
    publish(true, true);
    const published = accumulator;
    accumulator = incoming;
    return published;
}

/**
 * Close out the current view and start a fresh accumulator.
 *
 * KNOWN GAP, deliberately not closed: a pathname write that lands while a router transition
 * is still in flight — the outgoing page stays interactive and `pushEventUrl` writes event
 * urls from it — is a real view boundary, so the outgoing view is published correctly, but
 * the incoming view inherits the accumulator the transition split off. Blocking time
 * measured between the link click and that write is the destination route's chunk and data
 * work, and it ends up on the event view instead. Attributing it properly needs a third
 * accumulator (outgoing published, destination held for a commit still to come, incoming
 * starting now) and a way to tell `onBeforeViewChange` subscribers which kind of boundary
 * this is. That is more machinery than a rare overlap is worth; the view START of both
 * views is correct (see `bumpNavEpochOnPathChange`), which is what the vitals key off.
 */
function closeCurrentView(): void {
    clearFlushTimer();

    const hadStash = stashedOutgoing !== null;
    const published = publishOutgoingView();
    if (!hadStash) {
        // No router signal (popstate, or a bare history write): nothing was split off, so
        // the incoming view starts here.
        accumulator = newAccumulator(performance.now());
    }
    lastClosedView = published;
    lastClosedViewWasStashed = hadStash;

    dirty = accumulator.longTaskCount > 0;
    lastFlushPerfNow = -Infinity;
}

/**
 * The history write `closeCurrentView()` fired ahead of threw (WebKit rate-limits History
 * mutations), so the URL never moved and RUM's view never ended.
 *
 * The emitted vital can't be recalled, but the accumulators can be put back the way the
 * close found them — which is one of two shapes, depending on whether a router transition
 * was in flight at the time:
 *
 *  - No transition: the close published the current accumulator and started a fresh one.
 *    Fold that fresh one back into the published one and keep measuring on it, or the rest
 *    of this view's blocking time accumulates in an incoming view that never came in and
 *    its rollup overwrites the real view's `main_thread` context with a partial one.
 *  - Transition in flight: the close collapsed its split. Restore the split instead of
 *    merging — see the branch below.
 *
 * `vitalEmitted` rides along on the restored accumulator either way, so the view can't emit
 * a second `view_blocking_time` for the same RUM view.
 */
function revertViewChange(): void {
    const restored = lastClosedView;
    const wasStashed = lastClosedViewWasStashed;
    lastClosedView = null;
    if (!restored || restored === accumulator) return;
    drainObserver();

    if (wasStashed) {
        // A router transition was in flight, and the write that failed was not necessarily
        // its commit — the outgoing page stays interactive and writes event URLs of its own
        // (`pushEventUrl`). Put the split back exactly as it was rather than merging: the
        // current accumulator is the DESTINATION's, and folding it into the page being left
        // would bill that route's chunk and data work to the previous view and leave the
        // destination starting empty at its own commit.
        //
        // If the transition really did settle on this failed write, the aborted-transition
        // callback fires immediately after this one and collapses the split itself — which
        // is the same merge, done by the code that knows the transition is over.
        stashedOutgoing = restored;
        dirty = accumulator.longTaskCount > 0;
        return;
    }

    mergeAccumulators(restored, accumulator);
    accumulator = restored;
    dirty = accumulator.longTaskCount > 0;
}

function handleVisibilityChange(): void {
    if (document.visibilityState !== 'hidden') return;
    // Context only: the view may well resume. See `publish`.
    clearFlushTimer();
    publish(false, true);
}

function handlePageHide(): void {
    // A real end of the page — emit the closing vital. `pagehide` (unlike `unload`) fires
    // for bfcache entry too; if the page is later restored the accumulator keeps going and
    // its tail still reaches the view context, just not a second vital.
    clearFlushTimer();
    publishOutgoingView();
}

/**
 * Install the PerformanceObserver. Safe to call before RUM exists — entries accumulate
 * into a plain object and are published as soon as `startMainThreadPublishing()` runs.
 */
export function startMainThreadObservation(): void {
    if (observing || typeof window === 'undefined') return;
    observing = true;

    // Subscribed here rather than in `startMainThreadPublishing()`, and before any
    // early return, for two reasons — both about client navigations that happen before
    // the idle-deferred `initDatadog()`:
    //
    //  1. Without a view boundary the accumulator spans both pages, so the first
    //     `/markets/*` view is charged with blocking time from the page before it.
    //  2. `onBeforeViewChange` is what installs the facade's navigation tracking, which
    //     anchors `getCurrentViewStartPerfNow()`. Installed late, that stays 0 through
    //     the first navigation and `chart_ready` gets measured from the document's time
    //     origin — inflating the new view's vital with every page that preceded it.
    //
    // Pre-init, `closeCurrentView()`'s `publish()` is a no-op (nothing to publish into),
    // so this only segments the accumulator; it does not try to emit without RUM.
    unsubscribeBeforeViewChange = datadogRum.onBeforeViewChange(closeCurrentView);
    unsubscribeViewChangeReverted = datadogRum.onViewChangeReverted(revertViewChange);
    unsubscribeTransitionStart = datadogRum.onViewTransitionStart(handleTransitionStart);
    unsubscribeTransitionAborted = datadogRum.onViewTransitionAborted(handleTransitionAborted);

    if (typeof PerformanceObserver === 'undefined') {
        observerType = 'none';
        return;
    }

    const supported: readonly string[] = PerformanceObserver.supportedEntryTypes ?? [];
    if (supported.includes('long-animation-frame')) {
        observerType = 'loaf';
    } else if (supported.includes('longtask')) {
        observerType = 'longtask';
    } else {
        // Safari and Firefox support neither. Recorded rather than left null so a gap in
        // coverage reads as "unsupported browser" and not "instrumentation broken".
        observerType = 'none';
        return;
    }

    accumulator = newAccumulator(performance.now());

    try {
        observer = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) recordEntry(entry);
        });
        observer.observe({
            type: observerType === 'loaf' ? 'long-animation-frame' : 'longtask',
            // Buffered only for the document's FIRST observation, where the backlog is
            // exactly the cold-start work this instrumentation exists to catch. On a
            // re-init (disposeDatadog → AppEffectsBootstrap remount) the same backlog is
            // still in the performance timeline and has already been counted, so replaying
            // it would charge the first view after the remount with blocking time from
            // earlier in the document and inflate its `view_blocking_time`.
            buffered: !hasObservedInThisDocument,
        });
        hasObservedInThisDocument = true;
    } catch {
        observerType = 'none';
        observer = null;
    }
}

/** Attach RUM publication. Called from `initDatadog()` once the facade is live. */
export function startMainThreadPublishing(): void {
    if (publishing || typeof window === 'undefined') return;
    // Normally a no-op (instrumentation-client already ran it). Matters after a
    // `disposeDatadog()` → re-init cycle, which tears the observer down while
    // `instrumentation-client` only ever runs once per document.
    startMainThreadObservation();
    publishing = true;

    // The view boundary is already subscribed by `startMainThreadObservation()`, which
    // runs far earlier — see the note there.
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    // Everything the observer buffered before RUM existed belongs to this first view.
    if (accumulator.longTaskCount > 0) {
        dirty = true;
        scheduleFlush();
    }
}

/** Mirrors `disposeDatadog()` so a remount can re-init cleanly. */
export function stopMainThreadBlocking(): void {
    clearFlushTimer();
    observer?.disconnect();
    observer = null;
    observing = false;
    publishing = false;
    observerType = 'none';
    unsubscribeBeforeViewChange?.();
    unsubscribeBeforeViewChange = null;
    unsubscribeViewChangeReverted?.();
    unsubscribeViewChangeReverted = null;
    unsubscribeTransitionStart?.();
    unsubscribeTransitionStart = null;
    unsubscribeTransitionAborted?.();
    unsubscribeTransitionAborted = null;
    stashedOutgoing = null;
    lastClosedView = null;
    lastClosedViewWasStashed = false;
    vitalContextProvider = null;
    accumulator = newAccumulator(0);
    dirty = false;
    lastFlushPerfNow = -Infinity;
    if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('pagehide', handlePageHide);
    }
}

/**
 * Lets a higher layer (the event page's fingerprint) enrich the closing vital without
 * this module importing from `react_pages` — vitals are queried on their own, so they
 * need to carry enough context to be interpretable without a view join.
 */
export function setBlockingVitalContextProvider(provider: (() => Record<string, unknown>) | null): void {
    vitalContextProvider = provider;
}
